import { useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { WorkspaceId } from '@deckspace/ids';

export type SSEConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * Subscribe to real-time write events for a workspace via SSE.
 * Replaces Supabase Realtime. Debounces react-query invalidation.
 *
 * Uses delayed subscription for React Strict Mode (same pattern as
 * the original useRealtimeWorkspace).
 */
export function useWorkspaceEvents(
  apiUrl: string,
  workspaceId: WorkspaceId | undefined
): {
  connectionStatus: SSEConnectionStatus;
} {
  const queryClient = useQueryClient();
  const [connectionStatus, setConnectionStatus] = useState<SSEConnectionStatus>('connecting');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!workspaceId) return;

    setConnectionStatus('connecting');

    const url = `${apiUrl}/api/events?workspaceId=${encodeURIComponent(workspaceId)}`;
    const eventSource = new EventSource(url, { withCredentials: true });

    eventSource.onopen = () => {
      // Invalidate on (re)connect — ensures client catches up after any disconnect
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
        queryClient.invalidateQueries({ queryKey: ['datasets', workspaceId] });
      }, 300);
      setConnectionStatus('connected');
    };

    eventSource.onmessage = () => {
      // Debounce invalidation (300ms)
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] });
        queryClient.invalidateQueries({ queryKey: ['datasets', workspaceId] });
      }, 300);
    };

    eventSource.onerror = () => {
      setConnectionStatus('error');
    };

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      eventSource.close();
    };
  }, [apiUrl, workspaceId, queryClient]);

  return { connectionStatus };
}
